import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SessionType, ClientStatus } from '@shared';
import { selectSession, selectSessionAdvisor, selectMaybeClient } from '@/model';
import { useAdvisorNavigation } from '@/hooks/useAdvisorNavigation';
import { useClientSignOut } from '@/components/hooks/use-sign-out';
import { useNavigateToIntakeForm } from '@/hooks/useNavigateToIntakeForm';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';

export function useOnboardingHeader() {
  const advisor = useSelector(selectSessionAdvisor);
  const session = useSelector(selectSession);
  const client = useSelector(selectMaybeClient);
  const { clerkEmail } = useClerkClientAuth();
  const { navigateToClientsDashboard } = useAdvisorNavigation();
  const navigate = useNavigate();
  const handleSignOut = useClientSignOut();
  const { navigateToIntakeForm, isLoading: isLoadingForm } = useNavigateToIntakeForm();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const [isRequestMeetingOpen, setIsRequestMeetingOpen] = useState(false);
  const [isQuickEditOpen, setIsQuickEditOpen] = useState(false);

  const isClient = session.sessionType === SessionType.client;

  const hasFormReference = !!client?.formReferenceId;
  const isFormCompleted = client?.formReferenceStatus === 'completed';
  const isClientActive = client?.clientStatus === ClientStatus.active;
  const showIntakeFormButton =
    useFormProvider && hasFormReference && (isClient ? !isFormCompleted : true);
  const buttonText = isClientActive ? 'Update Intake Form' : 'Complete Intake Form';

  const intakeFormTooltip = isClient
    ? 'Update your intake form information to get more accurate predictions. Our AI models use your current health status, family history, and care preferences to personalize your long-term care predictions.'
    : "Update the client's intake form information to get more accurate predictions.";

  const onIntakeFormClick = isClient ? navigateToIntakeForm : () => setIsQuickEditOpen(true);
  const navigateToClientHome = () => navigate('/client/home');

  return {
    isClient,
    advisor,
    client,
    clientEmail: clerkEmail ?? '',
    navigateToClientsDashboard,
    navigateToClientHome,
    handleSignOut,
    isRequestMeetingOpen,
    openRequestMeeting: () => setIsRequestMeetingOpen(true),
    closeRequestMeeting: () => setIsRequestMeetingOpen(false),
    isQuickEditOpen,
    closeQuickEdit: () => setIsQuickEditOpen(false),
    showIntakeFormButton,
    buttonText,
    isLoadingForm,
    onIntakeFormClick,
    intakeFormTooltip,
  };
}
